<script setup>
import { defineProps, ref, computed } from 'vue'
import DocSection from '@/components/Docs/Section.vue'
import CodeBlock from '@/components/Docs/CodeBlock.vue'
import StringParam from '@/components/Docs/Parameters/StringParam.vue'
import MultiSelectParam from '@/components/Docs/Parameters/MultiSelectParam.vue'
import EndpointBlock from '@/components/Docs/EndpointBlock.vue'

defineProps({ anchor: String })

let request_query = ref("proto:tcp")
let request_format = ref("csv")
let request_log_types = ref(["conn"])

const logTypeOptions = [
  { value: 'conn', label: 'Connection Logs' },
  { value: 'dns', label: 'DNS Queries' },
  { value: 'http', label: 'HTTP Traffic' },
  { value: 'ssl', label: 'SSL/TLS' },
  { value: 'weird', label: 'Anomalies' },
  { value: 'x509', label: 'Certificates' },
  { value: 'files', label: 'File Transfers' },
  { value: 'notice', label: 'Notices' },
]

let request_body = computed(() => {
  return {
    query: request_query.value,
    log_types: request_log_types.value,
    format: request_format.value,
  }
})
</script>

<template>
  <doc-section title="Export search" :anchor="anchor">
    <template v-slot:left>
      <div class="mb-5">
        <p>
          Streams matching event documents to a file rather than returning a JSON
          envelope. Output is gzipped JSONL, gzipped CSV for a single log type, or a
          ZIP of CSVs when multiple log types match. Up to <strong>10,000</strong>
          documents are exported.
        </p>
        <p>
          Sources and log types are scoped to your organization's grants.
        </p>
      </div>

      <div class="mb-5">
        <h4 class="mb-4">Request parameters</h4>

        <StringParam class="mb-3" name="Query" :required="true"
          description="Query string selecting the documents to export"
          type="string" :value="request_query" @value="(v) => request_query = v" />

        <MultiSelectParam class="mb-3" name="Log Types" :required="false"
          description="Filter to specific log types (leave empty for all accessible types)"
          :options="logTypeOptions" :value="request_log_types" @value="(v) => request_log_types = v" />

        <StringParam class="mb-3" name="Format" :required="false"
          description="Output format: jsonl (default) or csv"
          type="string" :value="request_format" @value="(v) => request_format = v" />
      </div>
    </template>

    <template v-slot:right>
      <endpoint-block
        title="/query/event/search/export"
        method="POST"
        name="Export Search"
        query="/query/event/search/export"
        :body="request_body"
        :download="true"
      />

      <code-block title="Response (gzipped file)" raw>
ts,uid,id.orig_h,id.resp_h,proto
1704067200.12,CxYz123,192.168.1.100,8.8.8.8,tcp
1704067201.44,DaBc456,192.168.1.101,1.1.1.1,tcp
      </code-block>
    </template>
  </doc-section>
</template>
